import * as React from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@enginuity-apps-ui-components/atoms/button/button';
import { FormSelect } from '@enginuity-apps-ui-components/molecules/form-elements/form-select/form-select';
import { useState, useEffect } from 'react';
import { InfoPanel } from '@enginuity-apps-ui-components/molecules/form-elements/info-panel/info-panel';
import { FormInput } from '@enginuity-apps-ui-components/molecules/form-elements/form-input/form-input';
import { OptionsType } from 'react-select';
import { UserService } from '@enginuity-common/services/userService';
import { ErrorList } from '@enginuity-apps-ui-components/molecules/form-elements/error-list/error-list';
import { ErrorPanel } from '@enginuity-apps-ui-components/atoms/error-panel/error-panel';
import { createBrowserHistory } from 'history';
import { FormRadio } from '../../../../../ui-components/library/apps/molecules/form-elements/form-radio/form-radio';

export type IEngageOnboardingFormData = {
  qualification?: any;
  ageRange?: any;
  employmentStatus?: string;
  employer?: string;
  sector?: any;
};

export type IEngageOnboardingFormOptions = {
  qualification: OptionsType<any>[];
  ageRange: OptionsType<any>[];
  sector: OptionsType<any>[];
  employmentStatus: {
    [id: string]: { label: JSX.Element; value: any; };
  };
};

export type IEngageOnboardFormProps = {
  onSubmit: (data: IEngageOnboardingFormData) => void;
  onBack?: () => void;
  submitError?: JSX.Element;
  startNumber: number;
};

export default function EngageOnboardForm({ onSubmit, onBack, submitError, startNumber }: IEngageOnboardFormProps) {
  const { register, handleSubmit, errors, setValue, watch, triggerValidation } = useForm();
  const [onboardingOptions, setOnboardingOptions] = useState<IEngageOnboardingFormOptions | null>(null);
  const history = createBrowserHistory();

  const usersService = new UserService({
    baseUrl: window.spConfig.usersBaseUrl
  });

  const convertIncomingOnboardingOption = (incoming: string) => ({
    value: incoming,
    label: incoming,
  });

  const convertEmploymentStatusOptions = (opts: any[]): { [id: string]: { label: JSX.Element; value: any; }; } => {
    const newOpts: any = {};
    opts.forEach((incoming: string) => {
      newOpts[incoming] = {
        value: incoming,
        label: <>{incoming}</>,
      }
    })

    return newOpts;
  };

  const getData = async () => {
    const response = await usersService.getEngageOnboardingData();
    const data = await response.json();

    setOnboardingOptions({
      qualification: data.qualificationOptions.map(convertIncomingOnboardingOption),
      ageRange: data.ageRangeOptions.map(convertIncomingOnboardingOption),
      sector: data.sectorOptions.map(convertIncomingOnboardingOption),
      employmentStatus: convertEmploymentStatusOptions(data.employmentStatusOptions),
    });
  };

  const [qualificationSelected, setQualificationSelected] = useState({
    selectedOption: [],
  });
  const [ageRangeSelected, setageRangeSelected] = useState({
    selectedOption: [],
  });
  const [sectorSelected, setSectorSelected] = useState({
    selectedOption: [],
  });

  const handleQualificationChange = (selectedOption: any) => {
    setValue('qualification', selectedOption);
    setQualificationSelected({ selectedOption });
  };
  const handleAgeRangeChange = (selectedOption: any) => {
    setValue('ageRange', selectedOption);
    setageRangeSelected({ selectedOption });
  };
  const handleSectorChange = (selectedOption: any) => {
    setValue('sector', selectedOption);
    setSectorSelected({ selectedOption });
  };

  const employmentStatus = watch("employmentStatus");

  const handleEmployedChange = (event: React.FormEvent<HTMLInputElement>) => {
    setValue('employmentStatus', event.currentTarget.value);
    triggerValidation('employmentStatus');
  };


  useEffect(() => {
    getData();

    const unlistener = history.listen((location, action) => {
      if (action === "POP") {
        if (onBack) { onBack(); }
      }
    });

    register({ name: 'qualification' });
    register({ name: 'ageRange' });
    register({ name: 'sector' });
    register({ name: 'employmentStatus' }, { required: "Please enter your employment status." });
    return () => {
      unlistener();
    };
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="row md-spacer-32 spacer-64">
        <div className="col-lg-2"></div>
        <div className="col-lg-10">
          <h3 className="color-malachite-green">0{startNumber}. Personal details</h3>
        </div>
      </div>
      <div className="row md-spacer-32 spacer-16">
        <div className="col-lg-2"></div>
        <div className="col-lg-3">
          <FormSelect
            label={<>Highest qualification</>}
            id="qualification"
            value={qualificationSelected.selectedOption}
            options={onboardingOptions?.qualification}
            handleChange={handleQualificationChange}
            errors={errors}
            isSearchable={false}
          />
        </div>
        <div className="col-lg-3 lg-no-spacer spacer-16">
          <FormSelect
            label={<>Age range</>}
            id="ageRange"
            value={ageRangeSelected.selectedOption}
            options={onboardingOptions?.ageRange}
            handleChange={handleAgeRangeChange}
            errors={errors}
            isSearchable={false}
          />
        </div>
      </div>
      <div className="row md-spacer-32 spacer-72">
        <div className="col-lg-2"></div>
        <div className="col-lg-6">
          <div className="row no-spacer">
            <div className="col-lg-12">
              <h3 className="color-malachite-green">0{startNumber + 1}. Employment details</h3>
            </div>
          </div>
          <div className="row spacer-32">
            <div className="col-lg-12">
              <FormRadio
                label={<>Your employment status *</>}
                opts={onboardingOptions?.employmentStatus}
                name="employmentStatus"
                onChange={handleEmployedChange}
                errors={errors}
                selectedValue={employmentStatus}
              />
            </div>
          </div>
          {employmentStatus === "Employed" && (
            <div className="row spacer-16">
              <div className="col-lg-6">
                <FormInput
                  label={<>Please state your employers name *</>}
                  id="employer"
                  defaultValue=""
                  ref={register({
                    required: "Please enter your employer name.", maxLength: {
                      value: 512, message: 'Employer name cannot be more than 50 characters'
                    }
                  })}
                  errors={errors}
                />
              </div>
            </div>
          )}
          <div className="row spacer-16">
            <div className="col-lg-6">
              <FormSelect
                label={<>Sector</>}
                id="sector"
                value={sectorSelected.selectedOption}
                options={onboardingOptions?.sector}
                handleChange={handleSectorChange}
                errors={errors}
                isSearchable={false}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-4 why-collect-data-panel">
          <InfoPanel title={"Why do we collect this data?"} >
            <p>We have worked with the engineering industry to create a resource that aims to increase your skills and employability. This information will help us find you appropriate content and potential opportunities in the future.</p>
          </InfoPanel>
        </div>
      </div>
      <div className="row spacer-16">
        <div className="col-lg-2"></div>
        <div className="col-lg-10">
          <p className="required-field-text color-purple-grey">* indicates required field</p>
        </div>
      </div>
      <div className="row spacer-32">
        <div className="col-lg-2"></div>
        <div className="col-lg-10">
          <ErrorList errors={errors} >We found some errors. Please review the form and make the corrections.</ErrorList>
          {submitError && (
            <ErrorPanel>{submitError}</ErrorPanel>
          )}
        </div>
      </div>
      <div className="row spacer-32">
        <div className="col-lg-2"></div>
        <div className="col-lg-10">
          <Button buttonType="button" type="submit" className="cmp-button--primary">Next step</Button>
        </div>
      </div>
      {onBack && (
        <div className="row spacer-32">
          <div className="col-lg-2"></div>
          <div className="col-lg-10">
            <a href="#" onClick={onBack} className="link--secondary" tabIndex={0}>&lt;&nbsp;Back</a>
          </div>
        </div>)}
      <div className="row spacer-32">
      </div>
    </form>
  );
}
